import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import MaterialModal from "../MaterialModal";
import {
    GetAppSettingsList,
    SaveNewAppSetting,
    SaveNewDefault,
    GetAppSettings,
} from "../../../actions/appSettingsActions";
import { hideModal } from "../../../actions/actionCreators";
import withModal from "../withModal";
import h from "../../../helpers";
import TabPanel from "../../material-components/Misc/TabPanel";
import Select from "@material-ui/core/Select";
import FormControl from "@material-ui/core/FormControl";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import IconSwitch from "../../material-components/Misc/IconSwitch";
import InputLabel from "@material-ui/core/InputLabel";
import MenuItem from "@material-ui/core/MenuItem";
import TextField from "@material-ui/core/TextField";
import FormHelperText from "@material-ui/core/FormHelperText";
import Alert from "@material-ui/lab/Alert";
import Tabs from "@material-ui/core/Tabs";
import Tab from "@material-ui/core/Tab";
import Switch from "@material-ui/core/Switch";
import Checkbox from "@material-ui/core/Checkbox";
import { IAppState } from "../../../types/stores";
import { IAppSettingsList } from "../../../types/stores/appSettings";
import { IUserCompany } from "../../../types/stores/adminCompanies";
import NumericalTextField from "../../material-components/Misc/NumericalTextField";

type Props = {
    isOpen: boolean;
};

const NewAppSettingsModal: React.FC<Props> = ({ isOpen }: Props) => {
    const dispatch = useDispatch();

    // add new existing
    const [selectedCompany, setSelectedCompany] = useState("select");
    const [selectedSetting, setSelectedSetting] = useState("select");
    const [settingValue, setSettingValue] = useState("");
    const [settingDescription, setSettingDescription] = useState(" ");
    const [settingMaxLength, setSettingMaxLength] = useState(100);
    const [errorMessage, setErrorMessage] = useState("");
    // add new default
    const [defaultSettingName, setDefaultSettingName] = useState("");
    const [defaultSettingDescription, setDefaultSettingDescription] = useState("");
    const [defaultSettingMaxLength, setDefaultSettingMaxLength] = useState(100);
    const [defaultSettingActive, setDefaultSettingActive] = useState(false);
    const [defaultSettingValue, setDefaultSettingValue] = useState("");
    const [defaultErrorMessage, setDefaultErrorMessage] = useState("");
    const [maskSettingValue, setMaskSettingValue] = useState(false);
    // other
    const [selectedTab, setSelectedTab] = useState(0);

    const isLoadingSettingsList = useSelector<IAppState, boolean>(state => state.appSettings.isLoadingList || false);
    const settingsList = useSelector<IAppState, Array<IAppSettingsList>>(
        state => state.appSettings.appSettingsList || []
    );
    const companies = useSelector<IAppState, Array<IUserCompany>>(state => state.adminCompanies.userCompanies);
    const isSaving = useSelector<IAppState, boolean>(state => state.appSettings.isSaving);
    const enabledFeatures = useSelector<IAppState, Array<string>>(state => state.session.enabledFeatures || []);

    const handleTabChange = (event, newValue) => {
        event.target.value;
        setSelectedTab(newValue);
    };

    const hide = () => {
        dispatch(hideModal("ADD_APP_SETTING"));
    };

    const save = async () => {
        if (!selectedCompany || !selectedSetting || !settingValue || isSaving || isLoadingSettingsList) return;

        try {
            const res = await dispatch(
                SaveNewAppSetting(parseInt(selectedCompany), parseInt(selectedSetting), settingValue, maskSettingValue)
            );

            if (res) {
                setErrorMessage(res);
                return;
            }
            dispatch(GetAppSettings());
            hide();
        } catch (err) {
            h.error("Unexpected error occurred.", err);
        }
    };

    const saveNewDefault = async () => {
        if (!defaultSettingName) return;

        try {
            const res = await dispatch(
                SaveNewDefault(
                    defaultSettingName,
                    defaultSettingDescription,
                    defaultSettingActive,
                    defaultSettingMaxLength,
                    defaultSettingValue
                )
            );

            if (res) {
                setDefaultErrorMessage(res);
                return;
            }

            dispatch(GetAppSettings());

            hide();
        } catch (err) {
            h.error("Unexpected error occurred.", err);
        }
    };

    const handleSave = () => {
        switch (selectedTab) {
            case 0:
                save();
                break;
            case 1:
                saveNewDefault();
                break;
        }

        return;
    };

    // on init, get app settings list
    useEffect(() => {
        if (isOpen) {
            dispatch(GetAppSettingsList());
        }
        return () => {
            // reset on close
            setSelectedSetting("");
            setSettingDescription("");
            setSettingValue("");
            setSelectedCompany("");
            setErrorMessage("");
            setSettingMaxLength(100);
            setSelectedTab(0);
            setDefaultSettingName("");
            setDefaultSettingDescription("");
            setDefaultSettingMaxLength(100);
            setDefaultSettingActive(false);
            setDefaultErrorMessage("");
            setDefaultSettingValue("");
        };
    }, [isOpen, dispatch]);

    // on selected setting change
    useEffect(() => {
        if (selectedSetting) {
            const foundSetting = settingsList.find(setting => setting.SettingID == parseInt(selectedSetting));
            if (foundSetting) {
                setSettingDescription(foundSetting.SettingDescription || "No description available.");
                setSettingMaxLength(foundSetting.SettingMaxLength || 100);
            }
        }
    }, [selectedSetting, settingsList]);

    return (
        <MaterialModal
            title="Add New App Setting"
            isOpen={isOpen}
            onClose={hide}
            id="new-app-setting"
            maxWidth="lg"
            onPrimaryButtonRequest={handleSave}
            primaryButtonText={"Add"}
            isPrimaryButtonDisabled={isLoadingSettingsList || isSaving}
        >
            <Tabs value={selectedTab} indicatorColor="primary" textColor="primary" onChange={handleTabChange}>
                <Tab label="Add Setting to Company" />
                <Tab label="Add New Default Setting" />
            </Tabs>

            <TabPanel value={selectedTab} index={0}>
                {errorMessage && (
                    <Alert severity="error" style={{ marginTop: "15px" }}>
                        {errorMessage}
                    </Alert>
                )}
                <form onSubmit={save}>
                    <FormControl style={{ minWidth: "15vw", paddingRight: "10px", marginTop: "10px" }}>
                        <InputLabel>Company *</InputLabel>
                        <Select
                            disabled={isLoadingSettingsList || isSaving}
                            style={{ fontSize: "14px" }}
                            value={selectedCompany}
                            onChange={e => setSelectedCompany(e.target.value as string as string)}
                            required
                        >
                            <MenuItem disabled value={"select"}>
                                Select a company...
                            </MenuItem>
                            {companies &&
                                companies.length > 0 &&
                                companies.map((company, i) => (
                                    <MenuItem key={i} value={company.CompanyID}>
                                        {company.CompanyName}
                                        {company.CompanyActive == "N" ? " (Not Active)" : ""}
                                    </MenuItem>
                                ))}
                        </Select>
                    </FormControl>
                    <FormControl style={{ minWidth: "15vw", paddingRight: "10px", marginTop: "25px" }}>
                        <InputLabel>Setting Name *</InputLabel>
                        <Select
                            disabled={isLoadingSettingsList || isSaving}
                            style={{ fontSize: "14px" }}
                            value={selectedSetting}
                            onChange={e => setSelectedSetting(e.target.value as string)}
                            required
                        >
                            <MenuItem disabled value={"select"}>
                                Select a setting...
                            </MenuItem>
                            {settingsList &&
                                settingsList.length > 0 &&
                                settingsList.map((x, i) => (
                                    <MenuItem key={`${"type_"}${i}`} value={x.SettingID}>
                                        {x.SettingName}
                                    </MenuItem>
                                ))}
                        </Select>
                        <FormHelperText>{settingDescription}</FormHelperText>
                    </FormControl>
                    <TextField
                        disabled={isLoadingSettingsList || isSaving}
                        label="Setting Value"
                        margin="normal"
                        required
                        defaultValue={settingValue}
                        onChange={e => setSettingValue(e.target.value as string)}
                        style={{
                            fontSize: 16,
                            flexGrow: 1,
                            display: "flex",
                        }}
                        inputProps={{
                            maxLength: settingMaxLength,
                        }}
                    />
                    {enabledFeatures.includes("mask-app-setting") && (
                        <FormControlLabel
                            key="newIsMasked"
                            className="m-r-lg"
                            value={maskSettingValue}
                            checked={maskSettingValue}
                            control={<Checkbox color="primary" onChange={e => setMaskSettingValue(e.target.checked)} />}
                            label="Mask Setting Value"
                            labelPlacement="end"
                        />
                    )}
                </form>
            </TabPanel>
            <TabPanel value={selectedTab} index={1}>
                {defaultErrorMessage && (
                    <Alert severity="error" style={{ marginTop: "15px" }}>
                        {defaultErrorMessage}
                    </Alert>
                )}
                <form onSubmit={saveNewDefault}>
                    <TextField
                        disabled={isLoadingSettingsList || isSaving}
                        label="Setting Name"
                        margin="normal"
                        required
                        defaultValue={defaultSettingName}
                        onChange={e => setDefaultSettingName(e.target.value as string)}
                        style={{
                            fontSize: 16,
                            flexGrow: 1,
                            display: "flex",
                        }}
                        inputProps={{
                            maxLength: 100,
                        }}
                    />
                    <TextField
                        disabled={isLoadingSettingsList || isSaving}
                        label="Setting Description"
                        margin="normal"
                        defaultValue={defaultSettingDescription}
                        onChange={e => setDefaultSettingDescription(e.target.value as string)}
                        style={{
                            fontSize: 16,
                            flexGrow: 1,
                            display: "flex",
                        }}
                        inputProps={{
                            maxLength: 100,
                        }}
                        multiline
                        maxRows="10"
                    />
                    <NumericalTextField
                        disabled={isLoadingSettingsList || isSaving}
                        label="Setting Max-Length"
                        margin="normal"
                        required
                        value={defaultSettingMaxLength}
                        onBlur={e => setDefaultSettingMaxLength(parseInt(e.target.value))}
                        style={{
                            fontSize: 16,
                            flexGrow: 1,
                            display: "flex",
                        }}
                        inputProps={{
                            maxLength: 100,
                        }}
                    />
                    <TextField
                        disabled={isLoadingSettingsList || isSaving}
                        label="Setting Default Value"
                        margin="normal"
                        defaultValue={defaultSettingValue}
                        onChange={e => setDefaultSettingValue(e.target.value as string)}
                        style={{
                            fontSize: 16,
                            flexGrow: 1,
                            display: "flex",
                        }}
                        inputProps={{
                            maxLength: 100,
                        }}
                        helperText="Sets the default (Company ID - 0) setting value or else defaults to an empty string."
                    />
                    <FormControlLabel
                        control={
                            enabledFeatures.includes("") ? (
                                <IconSwitch
                                    id="default-active"
                                    checked={defaultSettingActive}
                                    onChange={e => setDefaultSettingActive(e.target.checked)}
                                    name="Active"
                                />
                            ) : (
                                <Switch
                                    required
                                    checked={defaultSettingActive}
                                    onChange={e => setDefaultSettingActive(e.target.checked)}
                                    name="Active"
                                />
                            )
                        }
                        label="Active *"
                    />
                </form>
            </TabPanel>
        </MaterialModal>
    );
};

NewAppSettingsModal.displayName = "NewAppSettingsModal";

export default withModal(NewAppSettingsModal, "ADD_APP_SETTING");
